/**
 * The GPU backend under the Pixi compositor (`webgl.ts`): WebGPU when the browser has it, WebGL
 * otherwise. `?gpu=webgl` or `?gpu=webgpu` forces one (a forced WebGPU that fails still falls back).
 * Both draw the same pixels: nearest sampling, premultiplied alpha, opaque canvas, no gamma step.
 */
import {
  isWebGPUSupported,
  WebGLRenderer,
  WebGPURenderer,
  type TextureSource,
  type WebGLOptions,
  type WebGPUOptions,
} from 'pixi.js';

export type GpuKind = 'webgl' | 'webgpu';
export type GpuRenderer = WebGLRenderer | WebGPURenderer;

function wanted(): GpuKind | null {
  const v = new URLSearchParams(globalThis.location?.search ?? '').get('gpu');
  return v === 'webgl' || v === 'webgpu' ? v : null;
}

export function gpuKind(renderer: GpuRenderer): GpuKind {
  return renderer instanceof WebGPURenderer ? 'webgpu' : 'webgl';
}

/**
 * A Pixi renderer on `canvas`. WebGL by default until the WebGPU path passes the pixel diff (it closed the page
 * on boot once on a real GPU); `?gpu=webgpu` tries WebGPU first, WebGL as the fallback.
 */
export async function createGpuRenderer(
  canvas: HTMLCanvasElement,
  options: Partial<WebGLOptions & WebGPUOptions>,
  kind?: GpuKind,
): Promise<GpuRenderer> {
  const force = kind ?? wanted();
  if (force === 'webgpu' && (await isWebGPUSupported({ powerPreference: 'high-performance' }))) {
    const renderer = new WebGPURenderer();
    try {
      await renderer.init({ ...options, canvas });
      watchDeviceLost(renderer);
      return renderer;
    } catch (e) {
      // Pixi asks for the adapter and device before it touches the canvas, so the canvas is still
      // free for a WebGL context here.
      console.warn('WebGPU init failed; using WebGL', e);
      try {
        renderer.destroy();
      } catch {
        /* half-made renderer */
      }
    }
  } else if (force === 'webgpu') console.warn('?gpu=webgpu: WebGPU is not available; using WebGL');
  const renderer = new WebGLRenderer();
  await renderer.init({ ...options, canvas });
  return renderer;
}

/**
 * A WebGPU device lost while playing (driver reset, GPU process crash) cannot draw again and the
 * canvas is taken by WebGPU, so the page reloads on WebGL (`?gpu=webgl`). Logged either way.
 */
function watchDeviceLost(renderer: WebGPURenderer): void {
  void renderer.gpu.device.lost.then((info) => {
    if (info.reason === 'destroyed') return; // our own `renderer.destroy()`
    console.error(`WebGPU device lost (${info.reason}): ${info.message}; reloading on WebGL`);
    const url = new URL(location.href);
    url.searchParams.set('gpu', 'webgl');
    location.replace(url);
  });
}

/**
 * Sends `pending` images into rectangles of a texture already on the GPU, without re-sending the
 * whole texture. Same alpha handling as Pixi's own upload on each backend.
 */
export function uploadRegions(
  renderer: GpuRenderer,
  source: TextureSource,
  pending: readonly { image: CanvasImageSource; x: number; y: number }[],
): void {
  if (renderer instanceof WebGPURenderer) {
    const texture = renderer.texture.getGpuSource(source);
    const premultipliedAlpha = source.alphaMode === 'premultiply-alpha-on-upload';
    const queue = renderer.gpu.device.queue;
    for (const { image, x, y } of pending) {
      const img = image as HTMLCanvasElement;
      queue.copyExternalImageToTexture(
        { source: img },
        { texture, origin: { x, y }, premultipliedAlpha },
        { width: img.width, height: img.height },
      );
    }
    return;
  }
  const gl = renderer.gl;
  // Unbound first so the bind below really binds it on the active unit 0: Pixi skips a bind it
  // believes is already in place without switching the active unit, so the sprites went into
  // whatever texture the last draw left active — figure frames went missing from the page
  // (flickering out each time their step came round) and another texture was scribbled over.
  renderer.texture.unbind(source);
  renderer.texture.bind(source, 0);
  // Pixi keeps its own record of this flag: put it back as found.
  const premultiplied = gl.getParameter(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL) as boolean;
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, source.alphaMode !== 'no-premultiply-alpha');
  for (const { image, x, y } of pending)
    gl.texSubImage2D(gl.TEXTURE_2D, 0, x, y, gl.RGBA, gl.UNSIGNED_BYTE, image as TexImageSource);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, premultiplied);
}

/** WGSL twin of `SHARP_VERTEX` + `SHARP_FRAGMENT` in `webgl.ts` (sharp bilinear world blit). */
export const SHARP_WGSL = /* wgsl */ `
struct GlobalUniforms {
  uProjectionMatrix: mat3x3<f32>,
  uWorldTransformMatrix: mat3x3<f32>,
  uWorldColorAlpha: vec4<f32>,
  uResolution: vec2<f32>,
}
struct LocalUniforms {
  uTransformMatrix: mat3x3<f32>,
  uColor: vec4<f32>,
  uRound: f32,
}
struct Blit {
  uSize: vec2<f32>,
  uScale: vec2<f32>,
  uOrigin: vec2<f32>,
  uFlip: vec2<f32>,
  uGlobe: vec4<f32>,
  uSkyLow: vec4<f32>,
  uSkyTop: vec4<f32>,
}
@group(0) @binding(0) var<uniform> globalUniforms: GlobalUniforms;
@group(1) @binding(0) var<uniform> localUniforms: LocalUniforms;
@group(2) @binding(0) var<uniform> blit: Blit;
@group(2) @binding(1) var uTexture: texture_2d<f32>;
@group(2) @binding(2) var uSampler: sampler;

struct VOut {
  @builtin(position) position: vec4<f32>,
  @location(0) vUV: vec2<f32>,
}

@vertex
fn mainVertex(@location(0) aPosition: vec2<f32>, @location(1) aUV: vec2<f32>) -> VOut {
  let mvp = globalUniforms.uProjectionMatrix * globalUniforms.uWorldTransformMatrix * localUniforms.uTransformMatrix;
  var out: VOut;
  out.position = vec4<f32>((mvp * vec3<f32>(aPosition, 1.0)).xy, 0.0, 1.0);
  out.vUV = aUV;
  return out;
}

@fragment
fn mainFragment(@builtin(position) fragCoord: vec4<f32>) -> @location(0) vec4<f32> {
  // Same arithmetic as SHARP_FRAGMENT: from the pixel centre, not the interpolated UV.
  let frag = vec2<f32>(fragCoord.x, blit.uFlip.x + blit.uFlip.y * fragCoord.y);
  var p = (frag - blit.uOrigin) / blit.uScale;
  // Globe View (globe.ts): the texel this bent pixel shows, or the sky over the horizon.
  if (blit.uGlobe.w > 0.5) {
    let X = p.x - blit.uGlobe.x;
    let Y = p.y - blit.uGlobe.y;
    let K = blit.uGlobe.z;
    let disc = 1.0 - 16.0 * K * (K * X * X - Y);
    if (disc < 0.0) {
      let up = 1.0 / (16.0 * K);
      let s = clamp((-Y - up) / max(blit.uGlobe.y - up, 1.0), 0.0, 1.0);
      return vec4<f32>(mix(blit.uSkyLow.rgb, blit.uSkyTop.rgb, s), 1.0);
    }
    p.y = blit.uGlobe.y + (sqrt(disc) - 1.0) / (8.0 * K);
  }
  // C0-ROTATE: hard texels (nearest), as SHARP_FRAGMENT: the seam blend beat into a dotted grid.
  p = floor(p) + vec2<f32>(0.5);
  let i = floor(p - 0.5);
  let f = p - 0.5 - i;
  let t = 1.0 / blit.uSize;
  let a = textureSampleLevel(uTexture, uSampler, (i + vec2<f32>(0.5, 0.5)) * t, 0.0);
  let b = textureSampleLevel(uTexture, uSampler, (i + vec2<f32>(1.5, 0.5)) * t, 0.0);
  let c = textureSampleLevel(uTexture, uSampler, (i + vec2<f32>(0.5, 1.5)) * t, 0.0);
  let d = textureSampleLevel(uTexture, uSampler, (i + vec2<f32>(1.5, 1.5)) * t, 0.0);
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
`;
